/**
 * chartSidebarStore.js  (plan 181 Stream A)
 *
 * A small vanilla "sidebar control bus" shared between the React chart-intelligence
 * tabs and the non-React `window.__brochart` command registry. NOT Zustand (not a
 * dependency here, and a plain module reads/writes more cleanly from both React and
 * a window adapter — matches the existing lib/hermesChats.js subscribe/emit pattern).
 *
 * Design choice (deviation from the plan's "move state into Zustand"): to keep the
 * LIVE user-facing chart low-risk, the React tabs KEEP owning their selection state
 * and logic. They merely:
 *   - publishSidebar({...}) their current state into this mirror (one-way), and
 *   - registerSidebarSetters({...}) their setter callbacks,
 * so external callers (the CLI via window.__brochart) can READ the state and DRIVE
 * the same setters the human UI uses. No destructive state migration.
 */

const state = {
  activeTab: 'symbols',
  analysts: { available: [], selected: [], direction: 'all' },
  patterns: { availableTypes: [], aggregates: {}, selected: [], direction: 'all', horizon: '3m' },
};

// Setter callbacks registered by the React components (the same handlers the human
// UI uses). Filled in via registerSidebarSetters; called by the __brochart handlers.
const setters = {};

const listeners = new Set();
function emit() { for (const fn of listeners) { try { fn(); } catch { /* ignore */ } } }

export function getSidebarState() { return state; }
export function subscribeSidebar(fn) { listeners.add(fn); return () => listeners.delete(fn); }

// One-way publish from a React component into the mirror. Shallow-merges the
// analysts/patterns blocks so a tab can update just its slice.
export function publishSidebar(partial) {
  if (!partial) return;
  if (partial.activeTab !== undefined) state.activeTab = partial.activeTab;
  if (partial.analysts) Object.assign(state.analysts, partial.analysts);
  if (partial.patterns) Object.assign(state.patterns, partial.patterns);
  emit();
}

export function registerSidebarSetters(obj) { Object.assign(setters, obj); }
export function getSidebarSetters() { return setters; }

// Resolve a {mode, values, current} selection into the next array.
// mode: 'set' (default) | 'add' | 'remove' | 'clear'
export function resolveSelection(current, values, mode = 'set') {
  const cur = Array.isArray(current) ? current : [];
  const vals = Array.isArray(values) ? values : (values != null ? [values] : []);
  switch (mode) {
    case 'clear':  return [];
    case 'add':    return [...new Set([...cur, ...vals])];
    case 'remove': return cur.filter(x => !vals.includes(x));
    case 'set':
    default:       return [...new Set(vals)];
  }
}
